import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import { get } from 'lodash';
import * as momentTimezone from 'moment-timezone';
import { TDateRangeName } from 'scw-digital-boards-widgets';
import { IDateRangePickerDate } from '../shared/components/date-range-picker/date-range-picker.interface';
import { IDropdownOption } from '../shared/components/mat-dropdown/mat-dropdown.component';
import { UserEffects } from '../state/user/user.effects';
import { EDateUnits, IDateUnits } from './date-utilities.interface';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);
dayjs.extend(weekday);
dayjs.extend(isoWeek);

export const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const API_DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';
export const DATE_FORMAT = 'YYYY-MM-DD';
export const MYSQL_TIMESTAMP_FORMAT_ZERO_SECOND = 'YYYY-MM-DD HH:mm:00';
export const MYSQL_TIMESTAMP_FORMAT_ZERO_MIN_AND_SEC = 'YYYY-MM-DD HH:00:00';
export const MILLISECONDS_IN_A_SECOND = 1000;
export const MILLISECONDS_IN_A_MINUTE = MILLISECONDS_IN_A_SECOND * 60;
export const MILLISECONDS_IN_AN_HOUR = MILLISECONDS_IN_A_MINUTE * 60;
export const MILLISECONDS_IN_A_DAY = MILLISECONDS_IN_AN_HOUR * 24;
export const MILLISECONDS_IN_A_MONTH = MILLISECONDS_IN_A_DAY * 30;
export const MILLISECONDS_IN_A_YEAR = MILLISECONDS_IN_A_DAY * 365;
export const rangeNameToDate: Record<TDateRangeName, () => IDateRangePickerDate> = {
  today: () => {
    return {
      startDate: getNowInUserTimezone().startOf('day'),
      endDate: getNowInUserTimezone().endOf('day'),
    };
  },
  yesterday: () => {
    return {
      startDate: getNowInUserTimezone().subtract(1, 'day').startOf('day'),
      endDate: getNowInUserTimezone().subtract(1, 'day').endOf('day'),
    };
  },
  last7Days: () => {
    return {
      startDate: getNowInUserTimezone().subtract(6, 'day').startOf('day'),
      endDate: getNowInUserTimezone().endOf('day'),
    };
  },
  last30Days: () => {
    return {
      startDate: getNowInUserTimezone().subtract(29, 'day').startOf('day'),
      endDate: getNowInUserTimezone().endOf('day'),
    };
  },
  thisWeek: () => {
    return {
      startDate: getNowInUserTimezone().startOf('week'),
      endDate: getNowInUserTimezone().endOf('week'),
    };
  },
  lastWeek: () => {
    return {
      startDate: getNowInUserTimezone().subtract(1, 'week').startOf('week'),
      endDate: getNowInUserTimezone().subtract(1, 'week').endOf('week'),
    };
  },
  thisMonth: () => {
    return {
      startDate: getNowInUserTimezone().startOf('month'),
      endDate: getNowInUserTimezone().endOf('month'),
    };
  },
  lastMonth: () => {
    return {
      startDate: getNowInUserTimezone().subtract(1, 'month').startOf('month'),
      endDate: getNowInUserTimezone().subtract(1, 'month').endOf('month'),
    };
  },
  thisYear: () => {
    return {
      startDate: getNowInUserTimezone().startOf('year'),
      endDate: getNowInUserTimezone().endOf('year'),
    };
  },
  lastYear: () => {
    return {
      startDate: getNowInUserTimezone().subtract(1, 'year').startOf('year'),
      endDate: getNowInUserTimezone().subtract(1, 'year').endOf('year'),
    };
  },
};

export class DateUtilitiesService {
  public static getTimezoneOptions(): IDropdownOption<string>[] {
    return momentTimezone.tz.names().reduce((names: IDropdownOption<string>[], name: string) => {
      if (name.includes('/')) {
        names.push({ id: name, name });
      }

      return names;
    }, []);
  }

  public static convertUserDateToUTCString(date: Dayjs): string {
    if (!get(date, '$x.$timezone')) {
      return dayjs.tz(date.format(API_DATE_TIME_FORMAT)).utc().format(API_DATE_TIME_FORMAT);
    }

    return date.utc().format(API_DATE_TIME_FORMAT);
  }

  public static convertUserDateStringToUTCString(date: string): string {
    return dayjs.tz(date, UserEffects.getUser().dfUserConfiguration.timezone).utc().format(API_DATE_TIME_FORMAT);
  }

  public static convertUTCToUserFormatted<ReturnType extends string | null>(
    dateToFormat: ReturnType,
    includeTime?: boolean,
    format?: string,
  ): ReturnType {
    if (dateToFormat === null) {
      return null as ReturnType;
    }

    const { timezone: userTimezone, dateFormat } = UserEffects.getUser().dfUserConfiguration;

    return dayjs
      .tz(dateToFormat, 'UTC')
      .tz(userTimezone)
      .format(format ? format : `${dateFormat}${includeTime ? ' LT' : ''}`) as ReturnType;
  }

  public static formatIntoUserPreferenceWithoutTimezoneChange<ReturnType extends string | null>(
    dateToFormat: ReturnType,
    includeTime?: boolean,
    format?: string,
  ): ReturnType {
    if (dateToFormat === null) {
      return null as ReturnType;
    }

    const { dateFormat } = UserEffects.getUser().dfUserConfiguration;

    return dayjs(dateToFormat).format(format ? format : `${dateFormat}${includeTime ? ' LT' : ''}`) as ReturnType;
  }

  public static convertUTCToUserTimezone(dateToFormat: string): Dayjs {
    return dayjs.tz(dateToFormat, 'UTC').tz(UserEffects.getUser().dfUserConfiguration.timezone);
  }

  public static buildDayjsFromISO8601DateString(dateString: string): Dayjs {
    return dayjs(dateString);
  }

  public static getNowInUTC(): Dayjs {
    return dayjs.utc();
  }

  public static getNowInUserTz(): Dayjs {
    return dayjs.tz(undefined, UserEffects.getUser().dfUserConfiguration.timezone);
  }

  public static calculateTimeDifferenceInMilliseconds(date1: Dayjs, date2: Dayjs): number {
    return date1.diff(date2);
  }

  public static getTimeUnitsInMilliseconds<U extends EDateUnits[]>(
    milliseconds: number,
    units: U,
  ): Pick<IDateUnits, U[number]> {
    const timeUnitMap = {
      [EDateUnits.years]: MILLISECONDS_IN_A_YEAR,
      [EDateUnits.months]: MILLISECONDS_IN_A_MONTH,
      [EDateUnits.days]: MILLISECONDS_IN_A_DAY,
      [EDateUnits.hours]: MILLISECONDS_IN_AN_HOUR,
      [EDateUnits.minutes]: MILLISECONDS_IN_A_MINUTE,
      [EDateUnits.seconds]: MILLISECONDS_IN_A_SECOND,
    };

    const result = {} as Pick<IDateUnits, U[number]>;

    let remainingMilliseconds = milliseconds;

    for (const unit of units) {
      const unitValue = Math.floor(remainingMilliseconds / timeUnitMap[unit]);
      result[unit as keyof typeof result] = unitValue;

      remainingMilliseconds = remainingMilliseconds % timeUnitMap[unit];
    }

    return result;
  }

  public static isDateLaterThenDate(date1: Dayjs, date2: Dayjs): boolean {
    return date1.isAfter(date2);
  }

  // noinspection D
  public static adjustDate(
    amount: 1 | -1,
    dates: IDateRangePickerDate<Dayjs> | null | undefined,
  ): IDateRangePickerDate<Dayjs> {
    if (!dates?.startDate || !dates?.endDate) {
      return { startDate: null, endDate: null };
    }

    const { startDate, endDate } = dates;
    const isSameYear = startDate.year() === endDate.year();

    // Exactly a specific year: Jan 1st to Dec 31st
    const isWholeYear =
      isSameYear &&
      startDate.month() === 0 &&
      startDate.date() === 1 &&
      endDate.month() === 11 &&
      endDate.date() === 31;

    // Exactly a specific month: 1st of the month to the last day of that exact month
    const isWholeMonth =
      isSameYear &&
      startDate.month() === endDate.month() &&
      startDate.date() === 1 &&
      endDate.date() === endDate.daysInMonth();

    // Exactly a specific week: 6 days apart, starting on either Sunday or Monday
    // Using startOf('day') ensures time differences don't throw off the day count
    const diffDays = endDate.startOf('day').diff(startDate.startOf('day'), 'day');
    const isSundayStartWeek = diffDays === 6 && startDate.day() === 0; // Sun -> Sat
    const isMondayStartWeek = diffDays === 6 && startDate.day() === 1; // Mon -> Sun
    const isWholeWeek = isSundayStartWeek || isMondayStartWeek;

    let newStartDate: Dayjs;
    let newEndDate: Dayjs;

    // 2. Apply the increment/decrement based on the matched rule
    if (isWholeYear) {
      newStartDate = startDate.add(amount, 'year');
      newEndDate = endDate.add(amount, 'year');
    } else if (isWholeMonth) {
      newStartDate = startDate.add(amount, 'month');
      // Snap to the last day of the new month to prevent Dayjs drift
      // (e.g. drifting from Jan 31 -> Feb 28 -> Mar 28 instead of Mar 31)
      newEndDate = endDate.add(amount, 'month').date(newStartDate.daysInMonth());
    } else if (isWholeWeek) {
      newStartDate = startDate.add(amount, 'week');
      newEndDate = endDate.add(amount, 'week');
    } else {
      newStartDate = startDate.add(amount, 'day');
      newEndDate = endDate.add(amount, 'day');
    }

    return { startDate: newStartDate, endDate: newEndDate };
  }
}

function getNowInUserTimezone(): Dayjs {
  return dayjs.tz(undefined, UserEffects.getUser().dfUserConfiguration.timezone);
}
